import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { MapPin, Globe, ExternalLink, Calendar, Store as StoreIcon, ArrowLeft } from 'lucide-react';
import Logo from '../../components/Logo';
import StoreMap from '../../components/StoreMap';

interface StoreInfo {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  website?: string;
  googleBusinessUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  logoUrl?: string | null;
  primaryColor?: string;
  subDescription?: string;
}

interface CampaignDetails {
  id: string;
  campaignId: string;
  campaignName: string;
  description?: string;
  imageUrl?: string | null;
  headerColor?: string;
  startDate: string;
  endDate: string;
  status: string;
  stores: StoreInfo[];
}

export default function CampaignPage() {
  const { runId } = useParams<{ runId: string }>();
  const [campaign, setCampaign] = useState<CampaignDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCampaign();
  }, [runId]);

  const loadCampaign = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: CampaignDetails }>(
        `/campaign/${runId}`
      );
      setCampaign(response.data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${startDate.toLocaleDateString(undefined, options)} - ${endDate.toLocaleDateString(undefined, options)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Campaign Not Found</h1>
          <p className="text-gray-500">{error || 'This campaign may have ended or is no longer available.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header
        className="py-6 px-4"
        style={{ backgroundColor: campaign.headerColor || '#0891b2' }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/">
              <Logo size="md" className="hover:opacity-80 transition-opacity" />
            </Link>
            <div>
              <span className="text-white/80 text-sm font-medium">Discover The Park</span>
              <Link
                to="/"
                className="flex items-center gap-1 text-white/60 hover:text-white text-xs transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to Home
              </Link>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white">{campaign.campaignName}</h1>
          <div className="flex items-center gap-2 mt-2 text-white/80">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">{formatDateRange(campaign.startDate, campaign.endDate)}</span>
          </div>
        </div>
      </header>

      {/* Campaign Image and Description */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {campaign.imageUrl && (
          <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
            <img
              src={campaign.imageUrl}
              alt={campaign.campaignName}
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {campaign.description && (
          <div className="mb-8">
            <p className="text-gray-700 text-lg leading-relaxed">{campaign.description}</p>
          </div>
        )}

        {/* Interactive Map Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-6 h-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">Store Locations</h2>
          </div>
          <p className="text-gray-600 mb-4">
            Find participating stores in the Severna Park area. Click on a marker to see store details!
          </p>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <StoreMap stores={campaign.stores} height="350px" />
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Click on markers to see store information. Scroll down for full details.
          </p>
        </div>

        {/* Participating Stores */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-6">
            <StoreIcon className="w-6 h-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">Participating Stores</h2>
          </div>

          <p className="text-gray-600 mb-6">
            Visit any of these {campaign.stores.length} participating stores to scan their QR code and enter the raffle!
          </p>

          <div className="grid gap-4">
            {campaign.stores.map((store) => (
              <div
                key={store.id}
                className={`bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow ${store.googleBusinessUrl ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  if (store.googleBusinessUrl) {
                    window.open(store.googleBusinessUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                <div className="p-4 sm:p-6">
                  <div className="flex items-start gap-4">
                    {/* Store Logo */}
                    {store.logoUrl ? (
                      <img
                        src={store.logoUrl}
                        alt={store.name}
                        className="w-16 h-16 object-contain rounded-lg flex-shrink-0 bg-white"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100">
                        <StoreIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}

                    {/* Store Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900">{store.name}</h3>
                      {(store.address || store.city) && (
                        <p className="text-gray-500 text-sm mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          {[store.address, store.city, store.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {store.subDescription && (
                        <p className="text-gray-600 text-sm mt-1">{store.subDescription}</p>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {store.googleBusinessUrl && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                            <MapPin className="w-4 h-4" />
                            Tap for Hours & Directions
                            <ExternalLink className="w-3 h-3" />
                          </span>
                        )}
                        {store.website && (
                          <a
                            href={store.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                          >
                            <Globe className="w-4 h-4" />
                            Website
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>Powered by Discover The Park</p>
        </div>
      </div>
    </div>
  );
}
